import React from 'react';
import { Dialog, DialogTitle, DialogContent, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, Rating } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface Question {
  id: number;
  title?: string;
  name?: string;
  question: string;
  rating: boolean;
  comment: boolean;
}

interface Section {
  id: number | string;
  name?: string;
  sectionName?: string;
}

interface SectionWithQuestions {
  section_id: string;
  question_ids: number[];
}

interface Form {
  id: number;
  name: string;
  section_title: string;
  question_title: string;
  rating_title: string;
  comment_title: string;
  sections_with_questions?: SectionWithQuestions[];
  designationGroup?: {
    id: number;
    name: string;
  };
  category?: {
    id: number;
    name: string;
  };
}

interface ViewFormDialogProps {
  open: boolean;
  onClose: () => void;
  formData: {
    form: Form;
    sections?: Section[];
    questions?: Question[];
  } | null;
}

const ViewFormDialog: React.FC<ViewFormDialogProps> = ({ open, onClose, formData }) => {
  if (!formData) {
    return null;
  }

  const { form, questions = [], sections = [] } = formData;

  // Group questions by sections if sections_with_questions exists
  const sectionsWithQuestions = form.sections_with_questions || [];

  // Create a map of section_id to section details
  const sectionMap = new Map();
  sections.forEach((section) => {
    sectionMap.set(String(section.id), section);
  });

  // Create grouped data for display
  const displayData: Array<{
    sectionId: string;
    sectionName: string;
    questions: Question[];
  }> = sectionsWithQuestions.map((sectionData) => {
    const section = sectionMap.get(sectionData.section_id);
    const sectionQuestions = questions.filter((q) => sectionData.question_ids.includes(Number(q.id)));

    return {
      sectionId: sectionData.section_id,
      sectionName: section?.sectionName || section?.name || `Section ${sectionData.section_id}`,
      questions: sectionQuestions,
    };
  });

  // If no sections_with_questions, show all questions without grouping
  const flatQuestions = displayData.length === 0 ? questions : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          padding: '16px 24px',
        }}
      >
        <Typography variant="h6">{form.name}</Typography>
        <Button onClick={onClose} color="inherit" sx={{ minWidth: 'auto', p: 0.5 }}>
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px' }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f7fb' }}>
              <TableRow>
                <TableCell sx={{ width: '30%' }}>
                  <Typography variant="subtitle2">{form.section_title || 'Section'}</Typography>
                </TableCell>
                <TableCell sx={{ width: '40%' }}>
                  <Typography variant="subtitle2">{form.question_title || 'Questions'}</Typography>
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  <Typography variant="subtitle2" align="center">
                    {form.rating_title || 'Rating'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  <Typography variant="subtitle2">{form.comment_title || 'Comment'}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Render grouped sections and questions */}
              {displayData.map((sectionData) => (
                <React.Fragment key={sectionData.sectionId}>
                  {/* Questions for this section */}
                  {sectionData.questions.map((question, questionIndex) => (
                    <TableRow key={`${sectionData.sectionId}-${question.id}`}>
                      <TableCell>
                        {questionIndex === 0 && (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            {sectionData.sectionName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                          {question.question || 'No question text'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {question.rating ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Rating name={`rating-${question.id}`} value={0} readOnly size="small" />
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{question.comment ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}

              {/* Render flat questions if no sections grouping */}
              {flatQuestions.map((question) => (
                <TableRow key={question.id}>
                  <TableCell>
                    <Typography variant="body2">{question.title || question.name || `Question ${question.id}`}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{question.question || 'No question text'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {question.rating ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Rating name={`rating-${question.id}`} value={0} readOnly size="small" />
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{question.comment ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}

              {/* Show message if no data */}
              {displayData.length === 0 && flatQuestions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      No questions found for this form
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default ViewFormDialog;
